import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BounceLoader } from "react-spinners";
import styles from "./RefundTransactionModal.module.scss";
import CommonDialog from "../dialog/CommonDialog";
import Button from "../../ekedc-components/button/Button";
import { useGetRefundTransactionByReferenceForCustomerQuery } from "../../../service/transaction-service";
import { TRANSACTION_STATUS } from "../../../model/transaction";
import { formatDate, formatTime } from "../../../pages/customer/history/history.utils";
import { formatCurrency, handleCopy } from "../../../utils";
import { CopyIcon } from "../../../assets/icons";
import { ApplicationRoutes } from "../../../routes";
import checkmarkGif from "../../../assets/gifs/checkmark.gif";
import refundInProgressImg from "../../../assets/images/refund_in_progress.png";
import refundQueuedImg from "../../../assets/images/refund_queued.png";

interface RefundTransactionModalProps {
	// The original transaction's reference; the refund is looked up by it
	reference: string;
	onClose: () => void;
}

interface RefundRow {
	label: string;
	value: string;
	valueClass?: string;
	copyable?: boolean;
}

const formatDateTime = (value?: string | null) => (value ? `${formatDate(value)}, ${formatTime(value).toLocaleLowerCase()}` : "");

// Mirrors the mobile RefundTransactionContent
export const RefundTransactionModal = ({ reference, onClose }: RefundTransactionModalProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: refund, isLoading, error } = useGetRefundTransactionByReferenceForCustomerQuery({ Reference: reference });

	const isRefunded = refund?.status === TRANSACTION_STATUS.REFUNDED;
	const isInProgress = refund?.status === TRANSACTION_STATUS.IN_PROGRESS;
	const isQueued = refund?.status === TRANSACTION_STATUS.QUEUED_FOR_MANUAL_REVIEW;

	const goTo = (path: string) => {
		onClose();
		navigate(path);
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={styles.centered}>
					<BounceLoader color="var(--customer-primary)" />
				</div>
			);
		}

		if (error || !refund || !(isRefunded || isInProgress || isQueued)) {
			return (
				<div className={styles.centered}>
					<p className={styles.errorText}>{t("history.web.failedToLoadTransactionDetails")}</p>
				</div>
			);
		}

		// Refund amounts are stored as negative values
		const amount = formatCurrency(Math.abs(refund.amount.amount), { currency: refund.amount.currency });
		const walletName = refund.walletBankName ?? t("history.refund.wallet");
		const isRefundedToCard = refund.refundedTo === "Card";
		const refundedTo = isRefundedToCard ? t("history.refund.card") : walletName;

		let illustration: string;
		let heading: string;
		let message: string;
		let rows: RefundRow[];

		if (isRefunded) {
			illustration = checkmarkGif;
			heading = t("history.refund.successTitle");
			message = isRefundedToCard
				? t("history.refund.successCardMessage", { amount })
				: t("history.refund.successWalletMessage", { amount, walletName });
			rows = [
				{ label: t("history.refund.payment"), value: formatDateTime(refund.payment) },
				{ label: t("history.refund.refunded"), value: formatDateTime(refund.refunded) },
				{ label: t("history.refund.refundedTo"), value: refundedTo },
				...(refund.walletBalance != null
					? [{ label: t("history.refund.walletBalance"), value: formatCurrency(refund.walletBalance, { currency: refund.amount.currency }) }]
					: []),
			];
		} else if (isInProgress) {
			illustration = refundInProgressImg;
			heading = t("history.refund.inProgressTitle");
			message = t("history.refund.inProgressMessage");
			rows = [
				{ label: t("history.refund.amount"), value: amount },
				{ label: t("history.refund.refunded"), value: formatDateTime(refund.refunded) },
				{ label: t("history.refund.refundedTo"), value: refundedTo },
				{ label: t("history.refund.status"), value: t("history.refund.inProgress"), valueClass: styles.inProgress },
			];
		} else {
			illustration = refundQueuedImg;
			heading = t("history.refund.queuedTitle");
			message = t("history.refund.queuedMessage", { amount });
			rows = [
				{ label: t("history.refund.payment"), value: formatDateTime(refund.payment) },
				{ label: t("history.refund.attempt"), value: String(refund.numOfAttempt) },
				{ label: t("history.refund.status"), value: t("history.refund.queued"), valueClass: styles.queued },
				{ label: t("history.refund.amount"), value: amount },
			];
		}

		if (refund.paymentReference) {
			rows.push({ label: t("history.refund.paymentReference"), value: refund.paymentReference, copyable: true });
		}

		return (
			<>
				<div className={styles.hero}>
					<img src={illustration} alt="" className={styles.illustration} />
					<h3 className={styles.heading}>{heading}</h3>
					<p className={styles.message}>{message}</p>
				</div>

				<div className={styles.bottom}>
					<div className={styles.detailsCard}>
						{rows.map(row => (
							<div key={row.label} className={styles.row}>
								<span className={styles.label}>{row.label}</span>
								<span className={`${styles.value} ${row.valueClass ?? ""}`}>
									{row.value}
									{row.copyable && <CopyIcon onClick={() => handleCopy(row.value)} className={styles.copyBtn} />}
								</span>
							</div>
						))}
					</div>

					<div className={styles.actions}>
						{isQueued && <Button text={t("history.refund.contactSupport")} onClick={() => goTo(`/${ApplicationRoutes.CUSTOMER_SUPPORT_PAGE}`)} />}
						<Button variant={isQueued ? "secondary" : "primary"} text={t("common.pages.backToHome")} onClick={onClose} />
					</div>
				</div>
			</>
		);
	};

	return (
		<CommonDialog onCloseDialog={onClose} noPadding borderRadius="21.67px" bottomSheetOnMobile>
			<div className={styles.modal}>
				<div className={styles.body}>{renderContent()}</div>
			</div>
		</CommonDialog>
	);
};

export default RefundTransactionModal;
